/**
 * Jaipur Handloom storefront core — 18AE23
 *
 * Purpose
 * - Provide the shared JH namespace and reusable utilities.
 * - Own product-media switching, review-card expansion and cookie consent.
 * - Provide focus-management helpers used by drawers and modal dialogs.
 *
 * Dependency order
 * - Load this file before every other jh-*.js module.
 *
 * Maintenance rules
 * - Add shared helpers here instead of redefining query, route, money or debounce helpers.
 * - Do not add feature-specific cart, navigation, search or recommendation controllers here.
 * - Preserve public window.JH methods because later modules use them.
 */
(function() {
  'use strict';

  var JH = window.JH = window.JH || {};
  JH.version = '1.3.31.18AE26';

  var config = window.JHConfig || {};

  JH.Utils = {
    qs: function(selector, root) {
      return (root || document).querySelector(selector);
    },

    qsa: function(selector, root) {
      return Array.prototype.slice.call((root || document).querySelectorAll(selector));
    },

    ready: function(callback) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
      } else {
        callback();
      }
    },

    debounce: function(callback, wait) {
      var timer;
      return function() {
        var args = arguments;
        var context = this;
        window.clearTimeout(timer);
        timer = window.setTimeout(function() {
          callback.apply(context, args);
        }, wait || 250);
      };
    },

    escapeHtml: function(value) {
      return String(value == null ? '' : value).replace(/[&<>"']/g, function(character) {
        return {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        }[character];
      });
    },

    money: function(cents) {
      cents = Number(cents || 0);
      try {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents);
        }
      } catch (error) {}

      var currency = config.currency || 'USD';
      try {
        currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || currency;
      } catch (error) {}

      try {
        return (cents / 100).toLocaleString(undefined, {
          style: 'currency',
          currency: currency
        });
      } catch (error) {
        return (cents / 100).toFixed(2) + ' ' + currency;
      }
    },

    rootUrl: function() {
      var root = (config.routes && config.routes.root) || '/';
      try {
        root = (window.Shopify && Shopify.routes && Shopify.routes.root) || root;
      } catch (error) {}
      if (!root) root = '/';
      if (root.charAt(root.length - 1) !== '/') root += '/';
      return root;
    },

    route: function(path) {
      return JH.Utils.rootUrl() + String(path || '').replace(/^\/+/, '');
    },

    emit: function(name, detail, target) {
      try {
        (target || document).dispatchEvent(new CustomEvent(name, { detail: detail }));
      } catch (error) {}
    },

    idle: function(callback, timeout) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: timeout || 1000 });
      } else {
        window.setTimeout(callback, 160);
      }
    }
  };

  JH.A11y = {
    focusableSelector: [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(','),

    getFocusable: function(container) {
      if (!container) return [];
      return JH.Utils.qsa(JH.A11y.focusableSelector, container).filter(function(element) {
        return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null;
      });
    },

    activateDialog: function(dialog, opener, preferredFocus) {
      if (!dialog) return;
      dialog.__jhReturnFocus = opener || document.activeElement;
      dialog.setAttribute('aria-hidden', 'false');
      var target = preferredFocus || JH.A11y.getFocusable(dialog)[0] || dialog;
      window.setTimeout(function() {
        try { target.focus({ preventScroll: true }); } catch (error) { try { target.focus(); } catch (ignored) {} }
      }, 0);
    },

    deactivateDialog: function(dialog) {
      if (!dialog) return;
      dialog.setAttribute('aria-hidden', 'true');
      var returnFocus = dialog.__jhReturnFocus;
      dialog.__jhReturnFocus = null;
      if (returnFocus && document.contains(returnFocus)) {
        window.setTimeout(function() {
          try { returnFocus.focus({ preventScroll: true }); } catch (error) { try { returnFocus.focus(); } catch (ignored) {} }
        }, 0);
      }
    },

    trapTab: function(event, container) {
      if (!container || event.key !== 'Tab') return;
      var focusable = JH.A11y.getFocusable(container);
      if (!focusable.length) {
        event.preventDefault();
        container.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  JH.Media = {
    init: function(scope) {
      JH.Utils.qsa('[data-jh-media-gallery]', scope || document).forEach(function(gallery) {
        if (gallery.dataset.jhMediaReady === 'true') return;
        gallery.dataset.jhMediaReady = 'true';

        var main = JH.Utils.qs('[data-jh-main-media]', gallery);
        var thumbs = JH.Utils.qsa('[data-jh-thumb]', gallery);
        var index = Math.max(0, thumbs.findIndex(function(button) {
          return button.getAttribute('aria-current') === 'true';
        }));

        function show(nextIndex) {
          if (!thumbs.length || !main) return;
          if (nextIndex < 0) nextIndex = thumbs.length - 1;
          if (nextIndex >= thumbs.length) nextIndex = 0;

          var button = thumbs[nextIndex];
          var template = JH.Utils.qs('template', button);
          if (template) main.innerHTML = template.innerHTML;

          thumbs.forEach(function(item) {
            item.removeAttribute('aria-current');
          });
          button.setAttribute('aria-current', 'true');
          index = nextIndex;

          try {
            button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } catch (error) {}

          JH.Utils.emit('jh:media:changed', { index: index, button: button, gallery: gallery });
        }

        thumbs.forEach(function(button, buttonIndex) {
          button.addEventListener('click', function() {
            show(buttonIndex);
          });
        });

        var previous = JH.Utils.qs('[data-jh-media-prev]', gallery);
        var next = JH.Utils.qs('[data-jh-media-next]', gallery);
        if (previous) previous.addEventListener('click', function(event) {
          event.preventDefault();
          show(index - 1);
        });
        if (next) next.addEventListener('click', function(event) {
          event.preventDefault();
          show(index + 1);
        });
      });
    }
  };

  JH.Reviews = {
    init: function(scope) {
      JH.Utils.qsa('[data-jh-review-load]', scope || document).forEach(function(button) {
        if (button.dataset.jhReviewLoadReady === 'true') return;
        button.dataset.jhReviewLoadReady = 'true';
        button.addEventListener('click', function() {
          var grid = button.closest('.jh-reviews-full');
          var hiddenCards = grid ? JH.Utils.qsa('[data-jh-review-card].is-hidden', grid) : [];
          hiddenCards.slice(0, 8).forEach(function(card) {
            card.classList.remove('is-hidden');
          });
          if (grid && !JH.Utils.qsa('[data-jh-review-card].is-hidden', grid).length) button.remove();
        });
      });
    }
  };

  JH.Cookie = {
    init: function() {
      var box = JH.Utils.qs('[data-jh-cookie]');
      if (!box || box.dataset.jhCookieReady === 'true') return;
      box.dataset.jhCookieReady = 'true';

      var accepted = false;
      try { accepted = window.localStorage.getItem('jh_cookie_ok') === '1'; } catch (error) {}
      if (accepted) return;

      box.classList.add('is-open');
      var button = JH.Utils.qs('[data-jh-cookie-accept]', box);
      if (button) button.addEventListener('click', function() {
        try { window.localStorage.setItem('jh_cookie_ok', '1'); } catch (error) {}
        box.classList.remove('is-open');
      });
    }
  };

  JH.init = function(scope) {
    JH.Media.init(scope || document);
    JH.Reviews.init(scope || document);
    JH.Cookie.init();
  };

  JH.Utils.ready(function() {
    JH.init(document);
  });

  document.addEventListener('shopify:section:load', function(event) {
    JH.init(event.target);
  });
})();
